import { useQueryClient } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'
import DropdownLoading from 'common/components/DropdownLoading'
import ThaiDatePicker from 'common/components/ThaiDatePicker'
import {
  getFoodLineChartKey,
  getFoodTodayKey,
  getFoodsByRangeKey,
  getMealsByProductKey,
  getMealsKey,
  getRecentFoodByProductKey,
  getCostKey,
  getCostTransactionsHistoryInfiniteKey,
  STOCK_ID_KEY,
} from 'common/constants/queryKeys'
import useAnalytics, {
  AnalyticsEvents,
  ProductDomain,
  feedSourceValue,
} from 'common/hooks/useAnalytics'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { createArrayOfNumber } from 'common/utils/array'
import { formatDate } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import { errorMessageByField } from 'common/utils/hook-form'
import { ErrorEnumMessage } from 'core/api/constants/error_messages'
import { normalizeSpeciesName } from 'utils/analytics'
import endOfToday from 'date-fns/endOfToday'
import parseISO from 'date-fns/parseISO'
import {
  useMutateCreateMealByProduct,
  useMutateCreateNaturalProduct,
  useMutateCreateProduct,
  useMutateUpdateMealByProduct,
} from 'modules/food/api/mutation'
import {
  useGetFeedingTypes,
  useGetFoodBrands,
  useGetFoodSizes,
  useGetListProductByFood,
  useGetListProductByNaturalFood,
  useGetProteins,
  useGetRecentFoodByProduct,
} from 'modules/food/api/queries'
import { useFindMealById, useRecentMeal, useRecordedMeal } from 'modules/food/api/selector'
import { MAX_MEAL, MealStatus } from 'modules/food/constants'
import { selectedMealDateState, selectedMealIdState } from 'modules/food/stores/atom'
import { useInventoryDisplaySetting } from 'modules/inventory/api/selector'
import { useIsShrimpSpecie, useSpeciesName } from 'modules/pond/api/selector'
import BaseRecordDialog from 'modules/pond/components/BaseRecordDialog'
import { enqueueSnackbar } from 'notistack'
import { ChangeEvent, memo, useEffect, useState, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'
import * as Yup from 'yup'

import CloseIcon from '@mui/icons-material/Close'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  alpha,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Grid from '@mui/material/Grid'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'

import plusIcon from '@iconify/icons-ant-design/plus'
import { Icon } from '@iconify/react'

interface RecordFoodDialogProps {
  open: boolean
  onClose: () => void
  isEdit?: boolean
}

interface FormDataType {
  date: Date
  meal: number
  feedingType: number | undefined
  foodBrand: number
  stockId: string
  naturalStockId: string
  protein: number
  foodSize: number
  foodAmount: number | undefined
  mealStatus: string
  remainingFood: number | undefined
  foodStyle: { pellets: boolean; natural: boolean }
  naturalFoodType: string
  naturalFoodAmount: number | undefined
}

const INITIAL_VALUE: FormDataType = {
  date: new Date(),
  meal: -1,
  feedingType: 1,
  foodSize: -1,
  stockId: '',
  naturalStockId: '',
  foodBrand: -1,
  protein: -1,
  foodAmount: undefined,
  mealStatus: MealStatus.Finish,
  remainingFood: undefined,
  foodStyle: { pellets: false, natural: false },
  naturalFoodType: '',
  naturalFoodAmount: undefined,
}

const FoodEmptyState = ({ onAdd }: { onAdd: () => void }) => {
  const { t } = useTranslation('food')
  return (
    <Box>
      <Button
        fullWidth
        onClick={onAdd}
        startIcon={<Icon icon={plusIcon} width={20} height={20} />}
        sx={{
          border: '1px dashed',
          borderColor: 'success.main',
          borderRadius: 2,
          color: 'primary.main',
          fontWeight: 700,
          py: 1.5,
          bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.success.main, 0.16),
          },
        }}
      >
        {t('food.add')}
      </Button>
      <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 1 }}>
        <Icon
          icon="mdi:information-outline"
          width={16}
          height={16}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <Typography variant="caption" color="text.secondary">
          {t('food.empty_hint')}
        </Typography>
      </Stack>
    </Box>
  )
}

const RecordFoodDialog = ({ open, onClose, isEdit }: RecordFoodDialogProps) => {
  const { t } = useTranslation(['common', 'food', 'pond', 'auth'])
  const qc = useQueryClient()

  const [submitting, setSubmitting] = useState(false)
  const [openCreateProduct, setOpenCreateProduct] = useState(false)
  const [openCreateNaturalProduct, setOpenCreateNaturalProduct] = useState(false)

  const setDateState = useSetRecoilState(selectedMealDateState)
  const setMealId = useSetRecoilState(selectedMealIdState)
  const isShrimpSpecie = useIsShrimpSpecie()
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()

  const mealData = useFindMealById()

  const recentMeal = useRecentMeal()
  const recordedMeals = useRecordedMeal()

  const createMealMutation = useMutateCreateMealByProduct()
  const updateMealMutation = useMutateUpdateMealByProduct()

  const createProductMutation = useMutateCreateProduct()
  const createNaturalProductMutation = useMutateCreateNaturalProduct()

  // 1. Reusable Number Schema Helper
  const posNumber = (requiredMsg: string) =>
    Yup.number()
      .transform((value, original) => (original === '' || original === null ? undefined : value))
      .typeError(requiredMsg)
      .positive(t('food:amount.must.be.number'))
      .required(requiredMsg)

  const nonNegativeNumber = (requiredMsg: string) =>
    Yup.number()
      .transform((value, original) => (original === '' || original === null ? undefined : value))
      .typeError(requiredMsg)
      .min(0, t('food:amount.must.be.number'))
      .required(requiredMsg)

  // 2. Base Schema ที่ใช้ร่วมกัน
  const schema = Yup.object().shape({
    date: Yup.date(),
    meal: Yup.number().positive(t('food:require.meal')),
    feedingType: Yup.number()
      .positive(t('food:require.feeding.type'))
      .required(t('food:require.feeding.type')),
    mealStatus: Yup.string().when(['foodStyle.pellets'], {
      is: (pellets: boolean) => isShrimpSpecie || pellets === true,
      then: (s) => s.required(t('food:require.meal.status')),
      otherwise: (s) => s.nullable().notRequired(),
    }),

    // Pellets Logic: กุ้งต้องมีเสมอ หรือ ปลาต้องมีถ้าติ๊ก pellets
    stockId: Yup.string().when(['foodStyle.pellets'], {
      is: (pellets: boolean) => isShrimpSpecie || pellets === true,
      then: (s) => s.required(t('food:require.food.type')),
      otherwise: (s) => s.nullable().notRequired(),
    }),

    foodAmount: Yup.mixed().when(['foodStyle.pellets'], {
      is: (pellets: boolean) => isShrimpSpecie || pellets === true,
      then: () => nonNegativeNumber(t('food:require.amount')),
      otherwise: (s) => s.nullable().notRequired(),
    }),

    remainingFood: Yup.mixed().when(['foodStyle.pellets', 'mealStatus'], {
      is: (pellets: boolean, status: string) =>
        (isShrimpSpecie || pellets === true) && status === MealStatus.Remaining,
      then: () => posNumber(t('food:require.remaining.food')),
      otherwise: (s) => s.nullable().notRequired(),
    }),

    // ส่วนเสริมสำหรับบ่อปลา (Non-shrimp)
    ...(isShrimpSpecie
      ? {}
      : {
          foodStyle: Yup.object()
            .shape({
              natural: Yup.boolean(),
              pellets: Yup.boolean(),
            })
            .test('at-least-one', t('food:require.food.type'), (v) => v?.natural || v?.pellets),

          naturalStockId: Yup.string().when('foodStyle.natural', {
            is: true,
            then: (s) => s.required(t('food:require.food.type')),
          }),

          naturalFoodAmount: Yup.mixed().when('foodStyle.natural', {
            is: true,
            then: () => nonNegativeNumber(t('food:require.amount')),
          }),
        }),
  })
  const resolver = useYupValidationResolver(schema)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    getValues,
    formState: { errors, dirtyFields },
  } = useForm<FormDataType>({
    defaultValues: INITIAL_VALUE,
    resolver,
  })

  const {
    showLotNumber: showFoodLotNumber,
    showRemainingStock: showFoodRemainingStock,
    enforceStock,
  } = useInventoryDisplaySetting('food')

  const { data: feedingTypes, isFetching: isFeedingTypesLoading } = useGetFeedingTypes()
  const {
    data: products,
    isFetching: isProductLoading,
    refetch: refetchProducts,
  } = useGetListProductByFood()

  const {
    data: naturalProducts,
    isFetching: isNaturalProductLoading,
    refetch: refetchNaturalProducts,
  } = useGetListProductByNaturalFood()

  const { data: foodBrands, isFetching: isFoodBrandsLoading } = useGetFoodBrands()
  const { data: proteins, isFetching: isProteinLoading } = useGetProteins(watch('foodBrand'))
  const { data: foodSizes, isFetching: isFoodSizeLoading } = useGetFoodSizes(
    watch('foodBrand'),
    watch('protein')
  )

  const { data: recentFoodByProduct } = useGetRecentFoodByProduct()

  const allProducts = products?.flatMap((warehouse) => warehouse.products)
  const allNaturalProducts = naturalProducts?.flatMap((warehouse) => warehouse.products)

  // The active-products list (used for the dropdown options) never
  // includes soft-deleted stock, but an existing meal being edited may
  // still reference one. Without this, the Autocomplete would render
  // blank for that meal even though the meal record itself carries the
  // (deleted) product's name — inject it back in, grouped separately, so
  // it displays correctly labeled instead of disappearing.
  const editingProduct = isEdit ? mealData.currentMeal?.product : undefined
  const editingNaturalProduct = isEdit ? mealData.currentMeal?.product_natural : undefined

  const flatProducts = useMemo(() => {
    const base = (products ?? []).flatMap((warehouse) =>
      warehouse.products.map((product) => ({
        ...product,
        warehouse_name: warehouse.warehouse_name,
      }))
    )
    if (
      editingProduct?.stock_id &&
      !base.some((p) => p.stock_id === editingProduct.stock_id)
    ) {
      return [...base, { ...editingProduct, warehouse_name: t('food:food.deleted_group') }]
    }
    return base
  }, [products, editingProduct, t])

  const flatNaturalProducts = useMemo(() => {
    const base = (naturalProducts ?? []).flatMap((warehouse) =>
      warehouse.products.map((product) => ({
        ...product,
        warehouse_name: warehouse.warehouse_name,
      }))
    )
    if (
      editingNaturalProduct?.stock_id &&
      !base.some((p) => p.stock_id === editingNaturalProduct.stock_id)
    ) {
      return [
        ...base,
        { ...editingNaturalProduct, warehouse_name: t('food:food.deleted_group') },
      ]
    }
    return base
  }, [naturalProducts, editingNaturalProduct, t])

  const productDisplayName = (option: { name: string; stock_deleted_at?: string | null }) =>
    option.stock_deleted_at ? `${option.name} ${t('food:food.deleted_suffix')}` : option.name

  useEffect(() => {
    if (!isEdit) {
      const nextMeal = recentMeal + 1
      setValue('meal', nextMeal > MAX_MEAL ? MAX_MEAL : nextMeal)
    }
  }, [recentMeal])

  useEffect(() => {
    if (open && !isEdit && recentFoodByProduct) {
      resetToInitial()
    }
  }, [open, recentFoodByProduct, products, naturalProducts])

  const resetToInitial = () => {
    if (!recentFoodByProduct) return

    const currentValues = getValues()

    const defaultStockId = recentFoodByProduct.product?.stock_id ?? ''
    const defaultNaturalStockId = recentFoodByProduct.naturalProduct?.stock_id ?? ''

    let stockId = dirtyFields.stockId ? currentValues.stockId : ''
    let naturalStockId = dirtyFields.naturalStockId ? currentValues.naturalStockId : ''
    let feedingType = currentValues.feedingType ?? -1
    let mealStatus = currentValues.mealStatus ?? MealStatus.Finish
    let naturalFoodType = currentValues.naturalFoodType ?? ''

    // Map properties for standard pellets if user hasn't overridden it
    if (!dirtyFields.stockId) {
      feedingType = recentFoodByProduct.feedingType.id
      mealStatus = (recentFoodByProduct.status || MealStatus.Finish) as MealStatus

      const productExists = allProducts?.some((p) => p.stock_id === defaultStockId)
      stockId = productExists ? defaultStockId : ''
    }

    // Map properties for natural food if user hasn't overridden it
    if (!dirtyFields.naturalStockId) {
      const naturalProductExists = allNaturalProducts?.some(
        (p) => p.stock_id === defaultNaturalStockId
      )
      naturalStockId = naturalProductExists ? defaultNaturalStockId : ''

      naturalFoodType = recentFoodByProduct.naturalFoodType ?? ''
    }

    const nextMeal = recentMeal + 1

    const nextValues = {
      ...INITIAL_VALUE,
      ...currentValues,
      meal: nextMeal > MAX_MEAL ? MAX_MEAL : nextMeal,
      stockId,
      naturalStockId,
      feedingType,
      naturalFoodType,
      mealStatus,
      foodStyle: {
        pellets: !!stockId || currentValues.foodStyle.pellets,
        natural: !!naturalStockId || currentValues.foodStyle.natural,
      },
    }

    reset(nextValues)

    // Fallback overrides to explicitly push value to MUI controlled Selects
    if (stockId) setValue('stockId', stockId)
    if (naturalStockId) setValue('naturalStockId', naturalStockId)
  }

  useEffect(() => {
    if (mealData && isEdit) {
      const curMeal = mealData.currentMeal
      const date = mealData.date

      if (!date || !curMeal) return

      setDateState(parseISO(date))

      reset((_values) => ({
        date: parseISO(date),
        meal: curMeal.meal,
        feedingType: curMeal.feeding_type?.id,
        foodSize: 1,
        foodBrand: -1,
        stockId: curMeal.product?.stock_id,
        naturalStockId: curMeal.product_natural?.stock_id,
        foodAmount: curMeal.amount,
        mealStatus: curMeal.status,
        remainingFood: curMeal.remaining_food,
        protein: -1,
        foodStyle: {
          pellets: Boolean(curMeal.product),
          natural: Boolean(curMeal.product_natural),
        },
        naturalFoodType: '',
        naturalFoodAmount: curMeal.natural_amount ?? 0,
      }))
    }
  }, [mealData, isEdit, open])

  const handleRadioChange = (e: ChangeEvent<HTMLInputElement>, key: keyof FormDataType) => {
    const value = e.target.value
    const num = Number(value)
    const isNumber = !isNaN(num)
    if (!isNumber) {
      setValue(key, value)
      return
    }
    setValue(key, num)
  }

  const handleFoodStyleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked
    const name = e.target.name

    const foodStyle = {
      ...watch('foodStyle'),
      [name]: isChecked,
    }

    setValue('foodStyle', foodStyle)

    if (name === 'pellets' && !isChecked) {
      setValue('mealStatus', MealStatus.Finish)
      setValue('remainingFood', undefined)
      setValue('stockId', '')
      setValue('foodAmount', undefined)
    }

    if (name === 'natural' && !isChecked) {
      setValue('naturalStockId', '')
      setValue('naturalFoodAmount', undefined)
    }
  }

  const handleProductChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setValue('stockId', value)
    setValue('protein', -1)
    setValue('foodSize', -1)
  }

  const handleNaturalProductChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setValue('naturalStockId', value)
    setValue('naturalFoodType', '')
  }

  const handleFoodBrandChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const valueInt = Number(value)
    setValue('foodBrand', valueInt)

    setValue('protein', -1)
    setValue('foodSize', -1)
  }

  const handleProteinChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const valueInt = Number(value)
    setValue('protein', valueInt)

    setValue('foodSize', -1)
  }

  const handleFoodSizeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const valueInt = Number(value)
    setValue('foodSize', valueInt)
  }

  const resetCreateProductFields = () => {
    setValue('foodBrand', -1)
    setValue('protein', -1)
    setValue('foodSize', -1)
  }

  const handleOpenCreateProduct = () => {
    resetCreateProductFields()
    setOpenCreateProduct(true)
  }

  const handleCloseCreateProduct = () => {
    resetCreateProductFields()
    setOpenCreateProduct(false)
  }

  const resetCreateNaturalProductFields = () => {
    setValue('naturalFoodType', '')
  }

  const handleOpenCreateNaturalProduct = () => {
    resetCreateNaturalProductFields()
    setOpenCreateNaturalProduct(true)
  }

  const handleCloseCreateNaturalProduct = () => {
    resetCreateNaturalProductFields()
    setOpenCreateNaturalProduct(false)
  }

  const onSuccess = (res: AxiosResponse) => {
    const { status } = res.data
    if (status === 200) {
      enqueueSnackbar(t('food:record.success'), { variant: 'success' })
      setSubmitting(false)
      onClose()

      const { stockId, naturalStockId, foodStyle } = getValues()

      const normalizedSpecies = normalizeSpeciesName(speciesName)
      const feedSource = feedSourceValue(isShrimpSpecie || foodStyle.pellets, foodStyle.natural)
      trackEvent(isEdit ? AnalyticsEvents.feedRecordUpdated : AnalyticsEvents.feedRecordCreated, {
        ...(normalizedSpecies ? { species: normalizedSpecies } : {}),
        ...(feedSource ? { feed_source: feedSource } : {}),
      })

      reset(() => ({
        ...INITIAL_VALUE,
        stockId: '',
        naturalStockId: '',
        foodStyle: { pellets: false, natural: false },
      }))

      qc.invalidateQueries({ queryKey: getFoodTodayKey.all() })
      qc.invalidateQueries({ queryKey: getMealsKey.all() })
      qc.invalidateQueries({ queryKey: getMealsByProductKey.all() })
      qc.invalidateQueries({ queryKey: getFoodLineChartKey.all() })
      qc.invalidateQueries({ queryKey: getFoodsByRangeKey.all() })
      qc.invalidateQueries({ queryKey: getRecentFoodByProductKey.all() })
      qc.invalidateQueries({ queryKey: getCostKey.all() })
      qc.invalidateQueries({ queryKey: getCostTransactionsHistoryInfiniteKey.all() })
      if (stockId) qc.invalidateQueries({ queryKey: [...STOCK_ID_KEY, stockId] })
      if (naturalStockId) qc.invalidateQueries({ queryKey: [...STOCK_ID_KEY, naturalStockId] })
    }
  }

  const handleSelectMeal = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value
    const num = Number(value)
    setValue('meal', num)
    if (isEdit) {
      const meal = mealData.meals?.find((el) => el.meal === num)
      if (!meal) {
        reset((values) => ({
          ...INITIAL_VALUE,
          date: values.date,
          meal: values.meal,
          foodAmount: 0,
          price: 0,
        }))
        return
      }
      setMealId(meal?.id)
    }
  }

  const onError = (error: unknown, _variables: unknown, _context: unknown) => {
    const { response } = error as AxiosError<{ message?: string }>
    // 409 (เช่น STOCK_NOT_ENOUGH) แสดงผ่าน global axios interceptor อยู่แล้ว
    // (src/core/api/interceptors.ts) ไม่ต้อง handle ซ้ำที่นี่ ไม่งั้นจะเด้ง snackbar ซ้อนกัน 2 อัน
    if (response?.status === 400) {
      const rawMessage = response?.data?.message || ''
      const code = rawMessage.split(':').pop()?.trim() || ''
      const mapped = ErrorEnumMessage[code as keyof typeof ErrorEnumMessage]
      enqueueSnackbar(mapped ?? t('food:record.fail'), { variant: 'error' })
    }
  }

  const onSubmit = async (data: FormDataType) => {
    if (submitting) return
    setSubmitting(true)
    const hasPellets = isShrimpSpecie || data.foodStyle.pellets
    const commonBody = {
      status: hasPellets ? data.mealStatus : MealStatus.Finish,
      feedingTypeId: Number(data.feedingType),
      amount: hasPellets ? Number(data.foodAmount ?? 0) : 0,
      stockId: hasPellets ? data.stockId : null,
      ...(data.foodStyle.natural
        ? {
            stockNaturalId: data.naturalStockId,
            naturalAmount: data.naturalFoodAmount,
          }
        : {}),
      ...(hasPellets && data.mealStatus !== 'finish'
        ? { remainingFood: Number(data.remainingFood) }
        : {}),
    }

    isEdit
      ? updateMealMutation
          .mutateAsync(
            {
              mealId: mealData.currentMeal?.id || 0,
              body: commonBody,
            },
            { onSuccess, onError }
          )
          .finally(() => {
            setSubmitting(false)
          })
      : createMealMutation
          .mutateAsync(
            {
              body: {
                ...commonBody,
                date: formatDate(data.date),
                meal: data.meal,
              },
            },
            { onSuccess, onError }
          )
          .finally(() => {
            setSubmitting(false)
          })
  }

  const onCreateProduct = async () => {
    if (createProductMutation.isLoading) return
    if (watch('foodBrand') == -1 || watch('protein') == -1 || watch('foodSize') == -1) return

    try {
      const res = await createProductMutation.mutateAsync({
        body: {
          food_size_id: watch('foodSize'),
        },
      })

      if (res.status === 200) {
        enqueueSnackbar(t('common:save.success'), { variant: 'success' })

        await refetchProducts()

        setValue('stockId', res.data.data.stock_id, {
          shouldValidate: true,
          shouldDirty: true,
        })
        trackEvent(AnalyticsEvents.productCreated, { product_domain: ProductDomain.feed })
      }
    } catch (err) {
      console.error('Failed to create product:', err)
    } finally {
      handleCloseCreateProduct()
    }
  }

  const onCreateNaturalProduct = async () => {
    if (createNaturalProductMutation.isLoading) return
    if (watch('naturalFoodType') == '') return

    try {
      const res = await createNaturalProductMutation.mutateAsync({
        body: {
          natural_food_name: watch('naturalFoodType'),
        },
      })

      if (res.status === 200) {
        enqueueSnackbar(t('common:save.success'), { variant: 'success' })

        await refetchNaturalProducts()

        setValue('naturalStockId', res.data.data.stock_id, {
          shouldValidate: true,
          shouldDirty: true,
        })
        trackEvent(AnalyticsEvents.productCreated, { product_domain: ProductDomain.feed })
      }
    } catch (err) {
      console.error('Failed to create natural product:', err)
    } finally {
      handleCloseCreateNaturalProduct()
    }
  }

  const CreateProductByFood = () => {
    const brandId = watch('foodBrand')
    const proteinId = watch('protein')
    const sizeId = watch('foodSize')

    const selectedBrand = foodBrands?.find((b) => b.id === brandId)?.brand || ''
    const selectedProtein = proteins?.find((p) => p.id === proteinId)?.protein || ''
    const selectedSize = foodSizes?.find((s) => s.id === sizeId)?.size || ''

    const isShowResult = brandId !== -1 && proteinId !== -1 && sizeId !== -1
    const hasNoFoodProduct = !isProductLoading && flatProducts.length === 0

    return (
      <>
        <Grid item xs={12}>
          <Box sx={{ paddingTop: 1, mb: 1 }}>
            <Typography fontSize={16} fontWeight={600}>
              <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                *
              </Typography>
              {t('food:food')}
            </Typography>
          </Box>

          {hasNoFoodProduct ? (
            <FoodEmptyState onAdd={handleOpenCreateProduct} />
          ) : (
            <>
              <Controller
                name="stockId"
                control={control}
                render={({ field: { onChange, value, ref } }) => {
                  const selectedOption = flatProducts.find((opt) => opt.stock_id === value) || null

                  return (
                    <Autocomplete
                      options={flatProducts}
                      value={selectedOption}
                      getOptionDisabled={(option) =>
                        enforceStock && (option.stock_quantity ?? 0) <= 0
                      }
                      disabled={openCreateProduct}
                      loading={isProductLoading}
                      groupBy={(option) => option.warehouse_name}
                      getOptionLabel={productDisplayName}
                      onChange={(_event, newValue) => {
                        onChange(newValue ? newValue.stock_id : '')
                        setValue('protein', -1)
                        setValue('foodSize', -1)
                      }}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase()
                        return options.filter(
                          (opt) =>
                            opt.name.toLowerCase().includes(inputValue) ||
                            (opt.lot_number && opt.lot_number.toLowerCase().includes(inputValue))
                        )
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputRef={ref}
                          size="small"
                          placeholder={t('food:food.placeholder')}
                          error={!!errors.stockId}
                          helperText={errorMessageByField('stockId', errors)}
                          fullWidth
                        />
                      )}
                      renderGroup={(params) => (
                        <li key={params.key}>
                          <Box
                            sx={{
                              px: 2,
                              py: 1,
                              fontSize: 14,
                              fontWeight: 600,
                              color: 'text.secondary',
                              bgcolor: 'background.paper',
                            }}
                          >
                            {params.group}
                          </Box>
                          <ul style={{ padding: 0 }}>{params.children}</ul>
                        </li>
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.stock_id}>
                          <Box
                            sx={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              py: 0.5,
                            }}
                          >
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography fontWeight={700} color="text.primary">
                                  {productDisplayName(option)}
                                </Typography>
                                {enforceStock && (option.stock_quantity ?? 0) <= 0 && (
                                  <Chip
                                    label={t('food:food.out_of_stock')}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: 11,
                                      color: 'error.main',
                                      bgcolor: 'error.lighter',
                                    }}
                                  />
                                )}
                              </Box>
                              {showFoodLotNumber && (
                                <>
                                  <Box
                                    sx={{
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      borderRadius: 1,
                                      px: 1,
                                      py: 0.25,
                                      mt: 0.5,
                                      mr: 1,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      bgcolor: 'background.paper',
                                    }}
                                  >
                                    <Typography fontSize={12} color="text.secondary">
                                      {t('food:food.lot')}
                                    </Typography>
                                  </Box>
                                  {option.lot_number || '-'}
                                </>
                              )}
                            </Box>
                            {showFoodRemainingStock && (
                              <Typography fontSize={14} color="text.secondary">
                                {t('food:food.remaining')} {fNumber(option.stock_quantity)} {option.unit}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                    />
                  )
                }}
              />

              <Button
                sx={{
                  mt: 1,
                  color: 'primary.main',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 0,
                  '&:hover': { background: 'transparent' },
                }}
                onClick={handleOpenCreateProduct}
                disableRipple
              >
                <Icon icon={plusIcon} width={16} height={16} /> {t('food:food.add')}
              </Button>
            </>
          )}

          <Dialog
            open={openCreateProduct}
            onClose={handleCloseCreateProduct}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: 700,
                fontSize: 18,
              }}
            >
              {t('food:food.add')}
              <IconButton onClick={handleCloseCreateProduct} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ borderBottom: 'none' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ mb: 1 }}>
                    <Typography fontSize={16} fontWeight={600}>
                      <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                        *
                      </Typography>
                      {t('food:food.brand')}
                    </Typography>
                  </Box>
                  <Controller
                    name="foodBrand"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        size="small"
                        error={!!errors.foodBrand}
                        helperText={errorMessageByField('foodBrand', errors)}
                        fullWidth
                        sx={{
                          backgroundColor: (theme) => theme.palette.common.white,
                          borderRadius: 1,
                        }}
                        onChange={handleFoodBrandChange}
                      >
                        <MenuItem value={-1} disabled>
                          {t('food:food.placeholder')}
                        </MenuItem>
                        {isFoodBrandsLoading ? (
                          <DropdownLoading />
                        ) : (
                          foodBrands?.map((brand) => (
                            <MenuItem key={brand.id} value={brand.id}>
                              {brand.brand}
                            </MenuItem>
                          ))
                        )}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 1 }}>
                    <Typography fontSize={16} fontWeight={600}>
                      <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                        *
                      </Typography>
                      {t('food:food.protein')}
                    </Typography>
                  </Box>
                  <Controller
                    name="protein"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        size="small"
                        onChange={handleProteinChange}
                        error={!!errors.protein}
                        helperText={errorMessageByField('protein', errors)}
                        sx={{
                          backgroundColor: (theme) => theme.palette.common.white,
                          borderRadius: 1,
                        }}
                        fullWidth
                      >
                        <MenuItem value={-1} disabled>
                          {t('food:food.protein.placeholder')}
                        </MenuItem>
                        {isProteinLoading ? (
                          <DropdownLoading />
                        ) : (
                          proteins?.map((protein) => (
                            <MenuItem key={protein.id} value={protein.id}>
                              {protein.protein}
                            </MenuItem>
                          ))
                        )}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 1 }}>
                    <Typography fontSize={16} fontWeight={600}>
                      <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                        *
                      </Typography>
                      {t('food:food.size')}
                    </Typography>
                  </Box>
                  <Controller
                    name="foodSize"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        size="small"
                        onChange={handleFoodSizeChange}
                        error={!!errors.foodSize}
                        helperText={errorMessageByField('foodSize', errors)}
                        sx={{
                          backgroundColor: (theme) => theme.palette.common.white,
                          borderRadius: 1,
                        }}
                        fullWidth
                      >
                        <MenuItem value={-1} disabled>
                          {t('food:food.size.placeholder')}
                        </MenuItem>
                        {isFoodSizeLoading ? (
                          <DropdownLoading />
                        ) : (
                          foodSizes?.map((size) => (
                            <MenuItem key={size.id} value={size.id}>
                              {size.size}
                            </MenuItem>
                          ))
                        )}
                      </TextField>
                    )}
                  />
                </Grid>

                {isShowResult && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 1,
                        px: 1.5,
                        backgroundColor: '#E6F4FF',
                        border: '1px solid #91caff',
                        borderRadius: 1,
                        color: '#1890FF',
                      }}
                    >
                      <Typography sx={{ fontSize: 14 }}>
                        {t('food:food.result')} {selectedBrand} {selectedProtein} {selectedSize}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1, justifyContent: 'flex-end', gap: 1 }}>
              <Button
                onClick={handleCloseCreateProduct}
                variant="outlined"
                color="inherit"
                sx={{ borderRadius: 1, px: 3 }}
              >
                {t('common:cancel')}
              </Button>
              <LoadingButton
                color="primary"
                variant="contained"
                onClick={() => onCreateProduct()}
                disabled={!isShowResult}
                loading={createProductMutation.isLoading}
                sx={{ borderRadius: 1, px: 3 }}
              >
                {t('auth:confirm')}
              </LoadingButton>
            </DialogActions>
          </Dialog>
        </Grid>

        {!hasNoFoodProduct && (
          <Grid item xs={12}>
            <Box sx={{ paddingTop: 1, my: 1 }}>
              <Typography fontSize={16} fontWeight={600}>
                <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                  *
                </Typography>
                {t('food:food.amount')}
              </Typography>
            </Box>
            <Controller
              name="foodAmount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder={t('food:food.amount')}
                  size="small"
                  error={!!errors.foodAmount}
                  helperText={errorMessageByField('foodAmount', errors)}
                  InputProps={{
                    sx: {
                      width: 'fit-content',
                    },
                    endAdornment: <InputAdornment position="end">{t('food:kilo')}</InputAdornment>,
                  }}
                  fullWidth
                />
              )}
            />
          </Grid>
        )}
      </>
    )
  }

  const CreateProductByNaturalFood = () => {
    const hasNoNaturalProduct = !isNaturalProductLoading && flatNaturalProducts.length === 0

    return (
      <>
        {/* Create Product By Natural Food */}
        <Grid item xs={12}>
          <Box sx={{ paddingTop: 1, mb: 1 }}>
            <Typography fontSize={16} fontWeight={600}>
              <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                *
              </Typography>
              {t('food:food')}
            </Typography>
          </Box>

          {hasNoNaturalProduct ? (
            <FoodEmptyState onAdd={handleOpenCreateNaturalProduct} />
          ) : (
            <>
              <Controller
                name="naturalStockId"
                control={control}
                render={({ field: { onChange, value, ref } }) => {
                  const selectedOption =
                    flatNaturalProducts.find((opt) => opt.stock_id === value) || null

                  return (
                    <Autocomplete
                      options={flatNaturalProducts}
                      value={selectedOption}
                      getOptionDisabled={(option) =>
                        enforceStock && (option.stock_quantity ?? 0) <= 0
                      }
                      disabled={openCreateNaturalProduct}
                      loading={isNaturalProductLoading}
                      groupBy={(option) => option.warehouse_name}
                      getOptionLabel={productDisplayName}
                      onChange={(_event, newValue) => {
                        onChange(newValue ? newValue.stock_id : '')
                        setValue('naturalFoodType', '')
                      }}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase()
                        return options.filter(
                          (opt) =>
                            opt.name.toLowerCase().includes(inputValue) ||
                            (opt.lot_number && opt.lot_number.toLowerCase().includes(inputValue))
                        )
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputRef={ref}
                          size="small"
                          placeholder={t('food:food.placeholder')}
                          error={!!errors.naturalStockId}
                          helperText={errorMessageByField('naturalStockId', errors)}
                          fullWidth
                        />
                      )}
                      renderGroup={(params) => (
                        <li key={params.key}>
                          <Box
                            sx={{
                              px: 2,
                              py: 1,
                              fontSize: 14,
                              fontWeight: 600,
                              color: 'text.secondary',
                              bgcolor: 'background.paper',
                            }}
                          >
                            {params.group}
                          </Box>
                          <ul style={{ padding: 0 }}>{params.children}</ul>
                        </li>
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.stock_id}>
                          <Box
                            sx={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              py: 0.5,
                            }}
                          >
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography fontWeight={700} color="text.primary">
                                  {productDisplayName(option)}
                                </Typography>
                                {enforceStock && (option.stock_quantity ?? 0) <= 0 && (
                                  <Chip
                                    label={t('food:food.out_of_stock')}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: 11,
                                      color: 'error.main',
                                      bgcolor: 'error.lighter',
                                    }}
                                  />
                                )}
                              </Box>
                              {showFoodLotNumber && (
                                <>
                                  <Box
                                    sx={{
                                      border: '1px solid #e0e0e0',
                                      borderRadius: 1,
                                      px: 1,
                                      py: 0.25,
                                      mt: 0.5,
                                      mr: 1,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      bgcolor: 'background.paper',
                                    }}
                                  >
                                    <Typography fontSize={12} color="text.secondary">
                                      {t('food:food.lot')}
                                    </Typography>
                                  </Box>
                                  {option.lot_number || '-'}
                                </>
                              )}
                            </Box>
                            {showFoodRemainingStock && (
                              <Typography fontSize={14} color="text.secondary">
                                {t('food:food.remaining')} {fNumber(option.stock_quantity)} {option.unit}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                    />
                  )
                }}
              />

              <Button
                sx={{
                  mt: 1,
                  color: 'primary.main',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 0,
                  '&:hover': { background: 'transparent' },
                }}
                onClick={handleOpenCreateNaturalProduct}
                disableRipple
              >
                <Icon icon={plusIcon} width={16} height={16} /> {t('food:food.add')}
              </Button>
            </>
          )}

          <Dialog
            open={openCreateNaturalProduct}
            onClose={handleCloseCreateNaturalProduct}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
          >
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: 700,
                fontSize: 18,
              }}
            >
              {t('food:food.add')}
              <IconButton onClick={handleCloseCreateNaturalProduct} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ borderBottom: 'none' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ mb: 1 }}>
                    <Typography fontSize={16} fontWeight={600}>
                      <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                        *
                      </Typography>
                      {t('food:food.name')}
                    </Typography>
                  </Box>
                  <Controller
                    name="naturalFoodType"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder={t('food:food.name.placeholder')}
                        size="small"
                        error={!!errors.naturalFoodType}
                        helperText={errorMessageByField('naturalFoodType', errors)}
                        sx={{
                          backgroundColor: (theme) => theme.palette.common.white,
                          borderRadius: 1,
                        }}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 3, pt: 1, justifyContent: 'flex-end', gap: 1 }}>
              <Button
                onClick={handleCloseCreateNaturalProduct}
                variant="outlined"
                color="inherit"
                sx={{ borderRadius: 1, px: 3 }}
              >
                {t('common:cancel')}
              </Button>
              <LoadingButton
                color="primary"
                variant="contained"
                onClick={() => onCreateNaturalProduct()}
                disabled={watch('naturalFoodType') === ''}
                loading={createNaturalProductMutation.isLoading}
                sx={{ borderRadius: 1, px: 3 }}
              >
                {t('auth:confirm')}
              </LoadingButton>
            </DialogActions>
          </Dialog>
        </Grid>
        {!hasNoNaturalProduct && (
          <Grid item xs={12}>
            <Box sx={{ paddingTop: 1, my: 1 }}>
              <Typography fontSize={16} fontWeight={600}>
                <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                  *
                </Typography>
                {t('food:food.amount')}
              </Typography>
            </Box>

            <Controller
              name="naturalFoodAmount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder={t('food:food.natural.type.placeholder')}
                  size="small"
                  error={!!errors.naturalFoodAmount}
                  helperText={errorMessageByField('naturalFoodAmount', errors)}
                  fullWidth
                  InputProps={{
                    sx: {
                      width: 'fit-content',
                    },
                    endAdornment: <InputAdornment position="end">{t('food:kilo')}</InputAdornment>,
                  }}
                />
              )}
            />
          </Grid>
        )}
      </>
    )
  }

  const MealStatusBlock = () => (
    <Grid item xs={12}>
      <Box sx={{ paddingTop: 1, mb: 1 }}>
        <Typography fontSize={16} fontWeight={600}>
          <Typography component="span" color="error" sx={{ mr: 0.5 }}>
            *
          </Typography>
          {t('food:meal.status')}
        </Typography>
      </Box>

      <FormControl error={!!errors.mealStatus}>
        <RadioGroup
          row
          aria-labelledby="meal-status"
          name="mealStatus"
          defaultValue={MealStatus.Finish}
          value={watch('mealStatus')}
          onChange={(e) => handleRadioChange(e, 'mealStatus')}
        >
          <FormControlLabel
            value={MealStatus.Finish}
            control={<Radio />}
            label={t('food:out.of.food')}
          />
          <FormControlLabel
            value={MealStatus.Remaining}
            control={<Radio />}
            label={t('food:food.exist')}
          />
        </RadioGroup>
        <FormHelperText error={!!errors.mealStatus} sx={{ m: 0 }}>
          {errorMessageByField('mealStatus', errors)}
        </FormHelperText>
      </FormControl>

      {watch('mealStatus') === MealStatus.Remaining && (
        <Grid item xs={12} mt={1}>
          <Controller
            name="remainingFood"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                placeholder={t('food:spare.amount')}
                error={!!errors.remainingFood}
                helperText={errorMessageByField('remainingFood', errors)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('food:kilo')}</InputAdornment>,
                }}
              />
            )}
          />
        </Grid>
      )}
    </Grid>
  )

  return (
    <BaseRecordDialog
      title={t('food:food.record')}
      formId="record-meal"
      onClose={() => {
        onClose()
        if (!isEdit) resetToInitial()
      }}
      open={open}
      isSubmitting={submitting}
      disabledSubmit={openCreateProduct || openCreateNaturalProduct || submitting}
      submitButtonLabel={t('auth:confirm')}
    >
      <form id="record-meal" onSubmit={handleSubmit(onSubmit)}>
        <Grid
          container
          spacing={2}
          alignItems="flex-start"
          sx={{ '& .MuiGrid-item': { maxWidth: '100%' } }}
        >
          <Grid item xs={6}>
            <Box sx={{ paddingTop: 1, mb: 1 }}>
              <Typography fontSize={16} fontWeight={600}>
                <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                  *
                </Typography>
                {t('common:date')}
              </Typography>
            </Box>

            <Controller
              name="date"
              control={control}
              render={({ field: { ref, ...fieldParams } }) => (
                <Grid item xs={8}>
                  <ThaiDatePicker
                    {...fieldParams}
                    onChange={(value, _context) => {
                      if (!value) return

                      setValue('date', value)
                      setDateState(value)
                    }}
                    format="dd/MM/yyyy"
                    maxDate={endOfToday()}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: !!errors.date,
                        helperText: errorMessageByField('date', errors),
                        fullWidth: true,
                      },
                    }}
                    readOnly={isEdit}
                  />
                </Grid>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Box sx={{ paddingTop: 1, mb: 1 }}>
              <Typography fontSize={16} fontWeight={600}>
                <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                  *
                </Typography>
                {t('food:meal')}
              </Typography>
            </Box>

            <Controller
              name="meal"
              control={control}
              render={({ field }) => (
                <Grid item xs={8}>
                  <TextField
                    {...field}
                    onChange={handleSelectMeal}
                    select
                    size="small"
                    error={!!errors.meal}
                    helperText={errorMessageByField('meal', errors)}
                    fullWidth
                  >
                    <MenuItem value={-1} disabled>
                      {t('food:meal.placeholder')}
                    </MenuItem>
                    {createArrayOfNumber(MAX_MEAL).map((value) => {
                      const meal = value + 1
                      const isRecorded = recordedMeals.includes(meal)
                      return (
                        <MenuItem
                          key={value}
                          value={meal}
                          disabled={!isEdit ? isRecorded : !isRecorded}
                        >{`${t('food:number.meal')} ${meal}`}</MenuItem>
                      )
                    })}
                  </TextField>
                </Grid>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ paddingTop: 1, mb: 1 }}>
              <Typography fontSize={16} fontWeight={600}>
                <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                  *
                </Typography>
                {t('food:feeding.type.short')}
              </Typography>
            </Box>

            <FormControl error={!!errors.feedingType}>
              {isFeedingTypesLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="circular" width={18} height={18} />
                  <Skeleton width={50} />
                  <Skeleton variant="circular" width={18} height={18} />
                  <Skeleton width={50} />
                </Stack>
              ) : (
                <RadioGroup
                  row
                  aria-labelledby="feeding-type"
                  name="feedingType"
                  value={watch('feedingType')}
                  onChange={(e) => handleRadioChange(e, 'feedingType')}
                >
                  {feedingTypes?.map((el) => (
                    <FormControlLabel
                      key={el.id}
                      value={el.id}
                      control={<Radio />}
                      label={el.type}
                    />
                  ))}
                </RadioGroup>
              )}
              <FormHelperText error={!!errors.feedingType} sx={{ m: 0 }}>
                {errorMessageByField('feedingType', errors)}
              </FormHelperText>
            </FormControl>
          </Grid>

          {isShrimpSpecie ? (
            <>
              {/* Create Product By Food Component */}
              {CreateProductByFood()}
              {MealStatusBlock()}
            </>
          ) : (
            <>
              <Grid item xs={4}>
                <Box sx={{ paddingTop: 1, mb: 1 }}>
                  <Typography fontSize={16} fontWeight={600}>
                    <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                      *
                    </Typography>
                    {t('food:food.type')}
                  </Typography>

                  <FormHelperText error={!!errors.foodStyle} sx={{ m: 0 }}>
                    {errorMessageByField('foodStyle', errors)}
                  </FormHelperText>
                </Box>
              </Grid>

              <Grid item xs={12} display="flex" alignItems="center" gap={2}>
                <Box>
                  <FormControlLabel
                    label={t('food:food.pellet')}
                    control={
                      <Checkbox
                        name="pellets"
                        checked={watch('foodStyle.pellets') === true}
                        onChange={(event) => handleFoodStyleChange(event)}
                      />
                    }
                  />
                </Box>
                <Box>
                  <FormControlLabel
                    label={t('food:food.natural')}
                    control={
                      <Checkbox
                        name="natural"
                        checked={watch('foodStyle.natural') === true}
                        onChange={(event) => handleFoodStyleChange(event)}
                      />
                    }
                  />
                </Box>
              </Grid>

              {watch('foodStyle.pellets') === true && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ paddingTop: 1, mb: 1 }}>
                      <Typography fontSize={16} fontWeight={600}>
                        {t('food:food.pellet')}
                      </Typography>
                    </Box>
                    {CreateProductByFood()}
                    {MealStatusBlock()}
                  </Grid>
                </>
              )}

              {watch('foodStyle.natural') === true && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ paddingTop: 1, mb: 1 }}>
                      <Typography fontSize={16} fontWeight={600}>
                        {t('food:food.natural')}
                      </Typography>
                    </Box>
                    {CreateProductByNaturalFood()}
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </form>
    </BaseRecordDialog>
  )
}

export default memo(RecordFoodDialog)
